import React, { useEffect, useState } from 'react';

import {
  useGetDatesQuery,
  useGetPlanningPeriodsQuery,
  useGetTypicalMonthsQuery,
} from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import { selectProject, selectWorkflow } from 'state/workflowSlice';

import LoadingSpinner from '../spinner/Spinner';
import { workflowLookup } from '../workflow_common/workflowLookup';
import { useGetScenarioPeriodsQuery } from '../workflow_detour/ScenarioMonthPicker';

import styles from '../workspace/Workspace.module.css';

export enum ReliesOn {
  nrtDay,
  typicalMonth,
  planningMonth,
  detourMonth,
}

export function useHasNoRelevantData() {
  const project = useAppSelector(selectProject);
  const workflow = useAppSelector(selectWorkflow);
  const workflowReliesOn =
    workflowLookup[workflow]?.dataAvailableReliesOn || [];
  const [hasNoRelevantData, setHasNoRelevantData] = useState(false);

  const polling: any = {};
  if (hasNoRelevantData) {
    polling.pollingInterval = 30 * 60 * 1000; // Poll every 30 minutes
  }
  const { data: datesInfo } = useGetDatesQuery(project, {
    skip: !project || !workflowReliesOn.includes(ReliesOn.nrtDay),
    ...polling,
  });
  const { data: planningMonthsInfo } = useGetPlanningPeriodsQuery(project, {
    skip: !project || !workflowReliesOn.includes(ReliesOn.planningMonth),
    ...polling,
  });
  const { data: monthComparisonInfo } = useGetTypicalMonthsQuery(project, {
    skip: !project || !workflowReliesOn.includes(ReliesOn.typicalMonth),
    ...polling,
  });

  const { data: detourMonthsInfo } = useGetScenarioPeriodsQuery(project, {
    skip: !project || !workflowReliesOn.includes(ReliesOn.detourMonth),
    ...polling,
  });

  useEffect(() => {
    const hasNoNrtData =
      workflowReliesOn.includes(ReliesOn.nrtDay) &&
      datesInfo !== undefined &&
      datesInfo.length === 0;
    const hasNoPlanningMonths =
      workflowReliesOn.includes(ReliesOn.planningMonth) &&
      planningMonthsInfo !== undefined &&
      planningMonthsInfo.length === 0;
    const hasNoTypicalMonths =
      workflowReliesOn.includes(ReliesOn.typicalMonth) &&
      monthComparisonInfo !== undefined &&
      monthComparisonInfo.length === 0;
    const hasNoDetourMonths =
      workflowReliesOn.includes(ReliesOn.detourMonth) &&
      detourMonthsInfo !== undefined &&
      detourMonthsInfo.length === 0;
    setHasNoRelevantData(
      hasNoNrtData ||
        hasNoPlanningMonths ||
        hasNoTypicalMonths ||
        hasNoDetourMonths,
    );
  }, [
    datesInfo,
    planningMonthsInfo,
    monthComparisonInfo,
    detourMonthsInfo,
    workflowReliesOn,
  ]);
  return hasNoRelevantData;
}

export function NoDataLoadedWarning() {
  return (
    <div className={styles.nodata}>
      <LoadingSpinner large />
      <p>
        <b>Project data is currently loading, this may take several hours</b>
        <br />
        Please contact support@streetlightdata.com if you have any questions.
      </p>
    </div>
  );
}
